import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, onSnapshot, query, where, doc, runTransaction, getDocs } from 'firebase/firestore';
import { ConventionMetadata, ConShift, Prop, UserProfile, ConSignup } from '../../types';
import { cleanIcsString, safeFormatISO } from '../../lib/utils';
import { format, parseISO } from 'date-fns';
import { toZonedTime, format as formatTZ } from 'date-fns-tz';
import { toast } from 'sonner';

const TIME_ZONE = 'America/Chicago';
import { Calendar, User, Mail, CheckCircle, AlertTriangle, Clock, MapPin, Layout, Info, Phone, BadgeInfo, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';

interface ConVolunteerFormProps {
  conId: string;
  currentUserProfile?: UserProfile | null;
}

export default function ConVolunteerForm({ conId, currentUserProfile }: ConVolunteerFormProps) {
  const [convention, setConvention] = useState<ConventionMetadata | null>(null);
  const [shifts, setShifts] = useState<ConShift[]>([]);
  const [props, setProps] = useState<Prop[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [activeTab, setActiveTab] = useState<'shift' | 'register'>('shift');

  const [form, setForm] = useState({ name: '', email: '', needsBadge: '' as 'yes' | 'no' | '' });
  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);

  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '', sccId: '', shipName: '', canSetup: false, canTakedown: false });
  const [allShiftsData, setAllShiftsData] = useState<ConShift[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [mySignups, setMySignups] = useState<ConSignup[]>([]);

  useEffect(() => {
    if (currentUserProfile) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm(prev => ({ ...prev, name: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`, email: currentUserProfile.email }));
      setRegForm(prev => ({ ...prev, name: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`, email: currentUserProfile.email, phone: currentUserProfile.cell || '', sccId: currentUserProfile.sccNumber || '', shipName: currentUserProfile.shipName || '' }));
    }
  }, [currentUserProfile]);

  // Helper to format in Central Time
  const formatInTZ = (dateISO: string, formatStr: string) => {
    try {
      const zonedDate = toZonedTime(parseISO(dateISO), TIME_ZONE);
      return formatTZ(zonedDate, formatStr, { timeZone: TIME_ZONE });
    } catch (e) {
      return 'Invalid Time';
    }
  };

  const calculateStaffingCounts = () => {
    const uniquePropIds = Array.from(new Set(allShiftsData.map(s => s.propId)));
    const totalSetupNeeded = uniquePropIds.map(id => props.find(p => p.id === id)?.setupStaffNeeded || 0).reduce((a, b) => a + b, 0);
    const totalTakedownNeeded = uniquePropIds.map(id => props.find(p => p.id === id)?.takedownStaffNeeded || 0).reduce((a, b) => a + b, 0);
    
    const setupRegistered = registrations.filter(r => r.canSetup).length;
    const takedownRegistered = registrations.filter(r => r.canTakedown).length;

    return { totalSetupNeeded, totalTakedownNeeded, setupRegistered, takedownRegistered };
  };

  const { totalSetupNeeded, totalTakedownNeeded, setupRegistered, takedownRegistered } = calculateStaffingCounts();

  useEffect(() => {
    const unsubCon = onSnapshot(doc(db, 'conventions', conId), (snap) => {
      if (snap.exists()) {
        setConvention({ id: snap.id, ...snap.data() } as ConventionMetadata);
        setLoading(false);
      }
    });

    const unsubEvent = onSnapshot(doc(db, 'events', conId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.type === 'Convention') {
           setConvention(prev => {
             // If we already have a real convention doc, don't overwrite it
             if (prev && !prev.ownerId?.includes('command-deck')) return prev;
             
             return {
               id: snap.id,
               name: cleanIcsString(data.name),
               startDate: data.startDate,
               endDate: data.endDate || data.startDate,
               ownerId: 'command-deck',
               status: 'active',
               dailyHours: data.dailyHours || {},
               address: cleanIcsString(data.location || '')
             } as ConventionMetadata;
           });
           setLoading(false);
        }
      }
    });

    const unsubProps = onSnapshot(collection(db, 'props'), (snap) => {
      setProps(snap.docs.map(d => ({ id: d.id, ...d.data() } as Prop)));
    });

    const qShifts = query(collection(db, 'shifts'), where('conId', '==', conId));
    const unsubShifts = onSnapshot(qShifts, (snap) => {
      const allShifts = snap.docs.map(d => ({ id: d.id, ...d.data() } as ConShift));
      setAllShiftsData(allShifts);
      setShifts(allShifts
        .filter(s => {
          const count = s.assignedUserIds?.length || 0;
          const max = s.maxVolunteers || 0;
          return count < max;
        })
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
      );
    });

    const qRegs = query(collection(db, 'conRegistrations'), where('conId', '==', conId));
    const unsubRegs = onSnapshot(qRegs, (snap) => {
      setRegistrations(snap.docs.map(d => d.data()));
    });

    const qMySignups = currentUserProfile?.uid ? query(collection(db, 'signups'), where('conId', '==', conId), where('volunteerUid', '==', currentUserProfile.uid)) : null;
    const unsubMySignups = qMySignups ? onSnapshot(qMySignups, (snap) => {
      setMySignups(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConSignup)));
    }) : () => {};

    return () => {
      unsubCon();
      unsubEvent();
      unsubProps();
      unsubShifts();
      unsubRegs();
      unsubMySignups();
    };
  }, [conId]);

  const handleCancelShift = async (signupId: string, shiftId: string) => {
    if (!currentUserProfile?.uid) return;
    try {
      await runTransaction(db, async (transaction) => {
        const shiftRef = doc(db, 'shifts', shiftId);
        const shiftSnap = await transaction.get(shiftRef);
        if (shiftSnap.exists()) {
          const data = shiftSnap.data();
          const assignedIds = data.assignedUserIds || [];
          transaction.update(shiftRef, {
            assignedUserIds: assignedIds.filter((id: string) => id !== currentUserProfile.uid)
          });
        }
        transaction.delete(doc(db, 'signups', signupId));
      });
      toast.success('Duty assignment canceled.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel shift.');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'shift') {
      if (selectedShifts.length === 0) {
        toast.error('Please select at least one operational shift');
        return;
      }
      if (!form.needsBadge) {
        toast.error('Please specify if you require a badge');
        return;
      }
      setSubmitting(true);
      try {
        const cleanEmail = form.email.trim().toLowerCase();
        const safeEmail = btoa(cleanEmail).replace(/[/+=]/g, '_');

        await runTransaction(db, async (transaction) => {
          // 1. Read all selected shifts
          const shiftSnaps = await Promise.all(
            selectedShifts.map(shiftId => transaction.get(doc(db, 'shifts', shiftId)))
          );

          // 2. Validate all shifts
          for (let i = 0; i < shiftSnaps.length; i++) {
            const snap = shiftSnaps[i];
            if (!snap.exists()) throw new Error('Operational shift data stale. Please refresh.');
            
            const data = snap.data();
            const assignedIds = data.assignedUserIds || [];
            const maxVolunteers = data.maxVolunteers || 0;

            if (assignedIds.length >= maxVolunteers) {
              const shiftTime = formatInTZ(data.startTime, 'HH:mm');
              throw new Error(`Manual capacity limit detected. The shift starting at ${shiftTime} is now full. Please select a different shift.`);
            }
          }

          // 3. Write all signups and update shifts
          shiftSnaps.forEach((snap, idx) => {
            const shiftId = selectedShifts[idx];
            const signupId = `${conId}_${shiftId}_${safeEmail}`;
            const signupRef = doc(db, 'signups', signupId);

            transaction.set(signupRef, {
              shiftId,
              conId,
              volunteerName: form.name.trim(),
              volunteerEmail: cleanEmail,
              volunteerUid: currentUserProfile?.uid || null,
              needsBadge: form.needsBadge,
              timestamp: new Date().toISOString()
            });

            const data = snap.data();
            const assignedIds = data.assignedUserIds || [];
            transaction.update(snap.ref, {
              assignedUserIds: [...assignedIds, currentUserProfile?.uid || `VOL-${Date.now()}-${idx}`]
            });
          });
        });

        toast.success('Assignments confirmed. Welcome to the away team!');
        setCompleted(true);
      } catch (err: any) {
        if (err.message?.includes('Missing or insufficient permissions')) {
           toast.error('You are already registered for one or more of these operational shifts.');
        } else {
           toast.error(err.message || 'Transporter malfunction. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    } else {
      // General Registration
      if (!regForm.name || !regForm.email || !regForm.phone || !regForm.sccId || !regForm.shipName) {
         toast.error('All fields are required to register.');
         return;
      }
      setSubmitting(true);
      try {
        const cleanEmail = regForm.email.trim().toLowerCase();
        const safeEmail = btoa(cleanEmail).replace(/[/+=]/g, '_');
        const regId = `${conId}_${safeEmail}`;
        const regRef = doc(db, 'conRegistrations', regId);
        
        await runTransaction(db, async (transaction) => {
           transaction.set(regRef, {
              conId,
              name: regForm.name.trim(),
              email: cleanEmail,
              phone: regForm.phone.trim(),
              sccId: regForm.sccId.trim(),
              shipName: regForm.shipName.trim(),
              canSetup: regForm.canSetup,
              canTakedown: regForm.canTakedown,
              timestamp: new Date().toISOString()
           });
        });

        toast.success('Convention Registration Logged');
        setCompleted(true);
      } catch (err: any) {
        if (err.message?.includes('Missing or insufficient permissions')) {
          toast.error('Personnel with this subspace frequency are already registered.');
        } else {
          toast.error(err.message || 'Transporter malfunction.');
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (loading || !convention) {
    return (
      <div className="py-24 text-center">
        <Clock className="w-12 h-12 mx-auto animate-spin text-lcars-blue opacity-40 mb-4" />
        <p className="font-bold uppercase tracking-widest text-lcars-blue">Connecting to Subspace Registry...</p>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center space-y-6 animate-in zoom-in-95">
        <div className="w-24 h-24 bg-lcars-gold rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-lcars-gold/20">
          <CheckCircle className="w-14 h-14 text-black" />
        </div>
        <h2 className="text-4xl font-bold text-[var(--foreground)] uppercase tracking-tighter">
          {activeTab === 'shift' ? 'Duty Assignment Confirmed' : 'Registration Confirmed'}
        </h2>
        <p className="text-xl text-[var(--foreground)]/60 font-bold uppercase tracking-widest leading-relaxed">
          The recruitment of <span className="text-lcars-gold">{activeTab === 'shift' ? form.name : regForm.name}</span> has been logged.<br/>
          Check your subspace frequency for further mission briefings.
        </p>
        <button 
          onClick={() => { setCompleted(false); setForm({ name: '', email: '', needsBadge: '' }); setRegForm({name: '', email: '', phone: '', sccId: '', shipName: '', canSetup: false, canTakedown: false}); setSelectedShifts([]); }}
          className="lcars-pill bg-lcars-blue text-black px-12 py-3 font-bold uppercase transition-all hover:scale-105"
        >
          RETURN TO REGISTRY
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-lcars-gold text-black p-6 rounded-t-3xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tighter">{convention.name}</h1>
          <p className="font-bold text-sm tracking-widest opacity-80">STATION RECRUITMENT LOG</p>
        </div>
        <div className="flex flex-col items-end text-right">
          <div className="flex items-center gap-2 text-sm font-bold"><Calendar className="w-4 h-4"/> {convention.startDate && convention.endDate ? `${safeFormatISO(convention.startDate, 'MMM dd')} - ${safeFormatISO(convention.endDate, 'MMM dd, yyyy')}` : 'Dates TBD'}</div>
          <div className="flex items-center gap-2 text-xs font-bold opacity-60 hover:opacity-100 transition-opacity">
            <MapPin className="w-3 h-3"/> 
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(convention.address || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {convention.address || 'COORDINATES CLASSIFIED'}
            </a>
          </div>
          {convention.host && (
            <div className="text-[0.6rem] font-bold uppercase text-lcars-blue mt-1">Host: {convention.host}</div>
          )}
          {convention.volunteerCommLink && (
            <a href={convention.volunteerCommLink.startsWith('http') ? convention.volunteerCommLink : `https://${convention.volunteerCommLink}`} target="_blank" rel="noopener noreferrer" className="text-[0.6rem] font-bold uppercase text-lcars-gold mt-1 hover:underline">
              Join Volunteer Comms
            </a>
          )}
          {convention.photoAlbumLink && (
            <a href={convention.photoAlbumLink.startsWith('http') ? convention.photoAlbumLink : `https://${convention.photoAlbumLink}`} target="_blank" rel="noopener noreferrer" className="text-[0.6rem] font-bold uppercase text-lcars-purple mt-1 hover:underline">
              View Photo Album
            </a>
          )}
        </div>
      </div>

      {currentUserProfile && mySignups.length > 0 && (
        <div className="bg-lcars-blue/10 border border-lcars-blue rounded-xl p-6 mb-8 mt-4">
          <h2 className="text-lcars-blue font-bold uppercase text-lg mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" /> My Duty Roster
          </h2>
          <div className="space-y-3">
            {mySignups.map(signup => {
              const shift = allShiftsData.find(s => s.id === signup.shiftId);
              if (!shift) return null;
              return (
                <div key={signup.id} className="flex justify-between items-center bg-[var(--background)]/50 p-4 rounded border border-white/10">
                  <div>
                    <div className="font-bold text-[var(--foreground)] uppercase text-sm">
                      {formatInTZ(shift.startTime, 'EEE MMM dd')} | {formatInTZ(shift.startTime, 'HH:mm')} - {formatInTZ(shift.endTime, 'HH:mm')}
                    </div>
                    <div className="text-xs text-[var(--foreground)]/60 mt-1">Confirmed Assignment</div>
                  </div>
                  <button 
                    onClick={() => handleCancelShift(signup.id!, shift.id!)}
                    className="text-lcars-red hover:text-red-400 p-2"
                    title="Cancel Shift"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex font-bold text-sm uppercase mt-4 mb-2">
         <button 
           onClick={() => setActiveTab('shift')}
           className={`flex-1 py-3 transition-colors rounded-tl-lg ${activeTab === 'shift' ? 'bg-lcars-blue text-black' : 'bg-[var(--background)] text-lcars-blue border border-lcars-blue'}`}
         >
           Duty Roster Signup
         </button>
         <button 
           onClick={() => setActiveTab('register')}
           className={`flex-1 py-3 transition-colors rounded-tr-lg ${activeTab === 'register' ? 'bg-lcars-tan text-black' : 'bg-[var(--background)] text-lcars-tan border border-lcars-tan'}`}
         >
           General Convention Registration
         </button>
      </div>

      {(convention.pertinentInfo || (convention.dailyHours && Object.keys(convention.dailyHours).length > 0)) && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {convention.pertinentInfo && (
            <div className="md:col-span-2 bg-lcars-blue/10 border border-lcars-blue rounded-xl p-6">
              <h3 className="text-lcars-blue font-bold uppercase text-xs mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" /> Mission Briefing
              </h3>
              <p className="text-[var(--foreground)]/80 text-sm leading-relaxed italic whitespace-pre-wrap">
                {convention.pertinentInfo}
              </p>
            </div>
          )}
          {convention.dailyHours && Object.keys(convention.dailyHours).length > 0 && (
            <div className="bg-lcars-gold/10 border border-lcars-gold rounded-xl p-6 h-full flex flex-col">
              <h3 className="text-lcars-gold font-bold uppercase text-xs mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Opening Frequencies
              </h3>
              <div className="space-y-1 overflow-y-auto pr-2 scrollbar-hide flex-1">
                {Object.entries(convention.dailyHours as Record<string, { open: string; close: string }>)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([date, hours]) => (
                    <div key={date} className="flex justify-between text-[0.6rem] font-bold uppercase border-b border-white/5 py-1">
                      <span className="text-[var(--foreground)]/60">{date ? safeFormatISO(`${date}T12:00:00`, 'EEE MMM dd') : 'TBD'}</span>
                      <span className="text-lcars-gold whitespace-nowrap">{hours.open && hours.close ? `${hours.open} - ${hours.close}` : 'TBD'}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSignup} className="bg-white/5 border-x-8 border-b-8 border-lcars-gold p-8 rounded-b-[40px] space-y-12">
        {activeTab === 'shift' ? (
          <>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-8 w-2 bg-lcars-blue rounded-full"></div>
                <h2 className="text-xl font-bold text-[var(--foreground)] uppercase tracking-widest">Step 1: Identify Personnel</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ml-6">
                <div className="space-y-1">
                  <label htmlFor="assigned-name" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Assigned Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                    <Input 
                      id="assigned-name"
                      value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      required
                      className="bg-[var(--background)] border-lcars-blue pl-12 text-[var(--foreground)] h-12"
                      placeholder="e.g. Ensign Ro Laren"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label htmlFor="subspace-email" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Subspace Frequency (Email)</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                    <Input 
                      id="subspace-email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm({...form, email: e.target.value})}
                      required
                      className="bg-[var(--background)] border-lcars-blue pl-12 text-[var(--foreground)] h-12"
                      placeholder="name@starfleet.com"
                    />
                  </div>
                </div>
              </div>
              <div className="ml-6 space-y-2 mt-4 p-4 border border-lcars-blue/30 rounded-lg">
                <div className="text-[0.6rem] text-lcars-blue font-bold uppercase block mb-2">DO YOU REQUIRE A CONVENTION BADGE?</div>
                <div className="flex gap-4">
                  <button type="button" onClick={() => setForm({...form, needsBadge: 'yes'})} className={`flex-1 py-3 text-xs font-bold uppercase transition-all ${form.needsBadge === 'yes' ? 'bg-lcars-blue text-black' : 'bg-[var(--background)] border border-lcars-blue text-[var(--foreground)] hover:bg-white/5'}`}>YES, I NEED A BADGE</button>
                  <button type="button" onClick={() => setForm({...form, needsBadge: 'no'})} className={`flex-1 py-3 text-xs font-bold uppercase transition-all ${form.needsBadge === 'no' ? 'bg-lcars-blue text-black' : 'bg-[var(--background)] border border-lcars-blue text-[var(--foreground)] hover:bg-white/5'}`}>NO, I HAVE ONE</button>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-8 w-2 bg-lcars-tan rounded-full"></div>
                <h2 className="text-xl font-bold text-[var(--foreground)] uppercase tracking-widest">Step 2: Select Operational Shift</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 ml-6 overflow-y-auto max-h-[400px] pr-2 scrollbar-hide">
                {shifts.map(shift => {
                  const prop = props.find(p => p.id === shift.propId);
                  const count = shift.assignedUserIds?.length || 0;
                  const max = shift.maxVolunteers || 0;

                  return (
                    <button
                      key={shift.id}
                      type="button"
                      onClick={() => {
                        if (selectedShifts.includes(shift.id!)) {
                          setSelectedShifts(selectedShifts.filter(id => id !== shift.id!));
                        } else {
                          setSelectedShifts([...selectedShifts, shift.id!]);
                        }
                      }}
                      className={`p-4 border-2 transition-all text-left flex flex-col gap-1 relative ${selectedShifts.includes(shift.id!) ? 'bg-lcars-tan border-lcars-tan text-black' : 'bg-[var(--background)] border-white/10 text-[var(--foreground)] hover:border-lcars-tan/50'}`}
                    >
                      <div className="flex justify-between items-start w-full mb-1">
                        <div>
                          <div className="font-bold text-[0.65rem] uppercase text-[var(--foreground)]/60 group-hover:text-black/60">{formatInTZ(shift.startTime, 'EEE MMM dd')}</div>
                          <div className="font-black text-xs uppercase leading-tight mt-1">{prop?.name || 'Unknown Duty'}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedShifts.includes(shift.id!) ? 'border-black bg-[var(--background)]' : 'border-white/30'}`}>
                            {selectedShifts.includes(shift.id!) && <CheckCircle className="w-3 h-3 text-lcars-tan" />}
                          </div>
                          {count >= max - 1 && (
                            <AlertTriangle className="w-3 h-3 text-lcars-red mt-1" />
                          )}
                        </div>
                      </div>

                      <span className="text-lg font-bold">{formatInTZ(shift.startTime, 'HH:mm')} - {formatInTZ(shift.endTime, 'HH:mm')}</span>
                      <div className="flex justify-between items-center text-[0.6rem] mt-2 opacity-60 font-bold uppercase">
                        <span>Alpha Availability</span>
                        <span>{max - count} CREW</span>
                      </div>
                    </button>
                  );
                })}
                {shifts.length === 0 && (
                  <div className="col-span-full py-12 text-center bg-white/5 border border-white/10 rounded-lg">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-lcars-red" />
                    <p className="text-[var(--foreground)]/40 font-bold uppercase tracking-widest text-sm">All shifts have reached maximum capacity or are unassigned.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-8 w-2 bg-lcars-tan rounded-full"></div>
              <h2 className="text-xl font-bold text-[var(--foreground)] uppercase tracking-widest">Convention Registration Profile</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ml-6">
              <div className="space-y-1">
                <label htmlFor="reg-name" className="text-[0.6rem] text-lcars-tan font-bold uppercase">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                  <Input 
                    id="reg-name"
                    value={regForm.name}
                    onChange={e => setRegForm({...regForm, name: e.target.value})}
                    required
                    className="bg-[var(--background)] border-lcars-tan pl-12 text-[var(--foreground)] h-12"
                    placeholder="Ensign Ro Laren"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="reg-email" className="text-[0.6rem] text-lcars-tan font-bold uppercase">Subspace Frequency (Email)</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                  <Input 
                    id="reg-email"
                    type="email"
                    value={regForm.email}
                    onChange={e => setRegForm({...regForm, email: e.target.value})}
                    required
                    className="bg-[var(--background)] border-lcars-tan pl-12 text-[var(--foreground)] h-12"
                    placeholder="name@starfleet.com"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="reg-phone" className="text-[0.6rem] text-lcars-tan font-bold uppercase">Comlink Frequency (Phone)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                  <Input 
                    id="reg-phone"
                    type="tel"
                    value={regForm.phone}
                    onChange={e => setRegForm({...regForm, phone: e.target.value})}
                    required
                    className="bg-[var(--background)] border-lcars-tan pl-12 text-[var(--foreground)] h-12"
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="reg-scc" className="text-[0.6rem] text-lcars-tan font-bold uppercase">SCC Identification Number</label>
                <div className="relative">
                  <BadgeInfo className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                  <Input 
                    id="reg-scc"
                    value={regForm.sccId}
                    onChange={e => setRegForm({...regForm, sccId: e.target.value})}
                    required
                    className="bg-[var(--background)] border-lcars-tan pl-12 text-[var(--foreground)] h-12"
                    placeholder="SCC-123456"
                  />
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 space-y-1">
                <label htmlFor="reg-ship" className="text-[0.6rem] text-lcars-tan font-bold uppercase">Current Ship / Duty Station</label>
                <div className="relative">
                  <Info className="absolute left-3 top-2.5 w-5 h-5 text-[var(--foreground)]/20" />
                  <Input 
                    id="reg-ship"
                    value={regForm.shipName}
                    onChange={e => setRegForm({...regForm, shipName: e.target.value})}
                    required
                    className="bg-[var(--background)] border-lcars-tan pl-12 text-[var(--foreground)] h-12"
                    placeholder="USS Zavala"
                  />
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 space-y-4 mt-4 border-t border-white/10 pt-4">
                <h3 className="text-sm text-lcars-tan font-bold uppercase tracking-widest">Additional Staffing Availability</h3>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={() => setRegForm({...regForm, canSetup: !regForm.canSetup})}
                    className={`flex-1 p-4 border-2 transition-all text-left flex flex-col gap-1 relative ${regForm.canSetup ? 'bg-lcars-blue border-lcars-blue text-black' : 'bg-[var(--background)] border-white/10 text-[var(--foreground)] hover:border-lcars-blue/50'}`}
                  >
                    <div className="font-bold uppercase flex items-center justify-between">
                      <span>Setup Crew</span>
                      {regForm.canSetup && <CheckCircle className="w-5 h-5" />}
                    </div>
                    <div className={`text-xs uppercase font-bold mt-2 ${regForm.canSetup ? 'text-black/70' : 'text-lcars-blue'}`}>{setupRegistered} / {totalSetupNeeded} Registered</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRegForm({...regForm, canTakedown: !regForm.canTakedown})}
                    className={`flex-1 p-4 border-2 transition-all text-left flex flex-col gap-1 relative ${regForm.canTakedown ? 'bg-lcars-red border-lcars-red text-black' : 'bg-[var(--background)] border-white/10 text-[var(--foreground)] hover:border-lcars-red/50'}`}
                  >
                    <div className="font-bold uppercase flex items-center justify-between">
                      <span>Takedown Crew</span>
                      {regForm.canTakedown && <CheckCircle className="w-5 h-5" />}
                    </div>
                    <div className={`text-xs uppercase font-bold mt-2 ${regForm.canTakedown ? 'text-black/70' : 'text-lcars-red'}`}>{takedownRegistered} / {totalTakedownNeeded} Registered</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="pt-8 border-t border-white/10">
          <button 
            type="submit"
            disabled={submitting}
            className={`w-full h-16 lcars-pill flex items-center justify-center gap-3 text-xl font-bold uppercase transition-all ${submitting ? 'bg-white/10 text-[var(--foreground)]/20' : 'bg-lcars-blue text-black hover:scale-[1.02]'}`}
          >
            {submitting ? 'PROCESSING RECRUITMENT...' : (activeTab === 'shift' ? 'CONFIRM DUTY ASSIGNMENT' : 'SUBMIT REGISTRATION')}
          </button>
          <p className="text-center text-[0.6rem] text-[var(--foreground)]/20 font-bold uppercase tracking-[0.2em] mt-4">
             {activeTab === 'shift' 
               ? 'By clicking confirm, you agree to report for duty at the designated stardate and coordinates.'
               : 'By clicking submit, your identification pattern will be locked into the convention registry.'
             }
          </p>
        </div>
      </form>
    </div>
  );
}
